import { SyntheticEvent, useState } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Stack, Tab, Typography as T } from "@mui/material";
import useDataset from "datasets/hooks/useDataset";
import AgentInstructionsEditor from "datasets/pages/settings/components/AgentInstructionsEditor";
import ConversationShortcutsTab from "datasets/pages/settings/components/ConversationShortcutsTab";
import DatasetSkillsTab from "datasets/pages/settings/components/DatasetSkillsTab";
import McpServerToggles from "datasets/pages/settings/components/McpServerToggles";
import OverviewTab from "datasets/pages/settings/components/OverviewTab";
import SamplePromptsTab from "datasets/pages/settings/components/SamplePromptsTab";
import useInitializationStatus from "datasets/pages/settings/hooks/useInitializationStatus";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import Switch from "~/components/Switch";
import useErrorToast from "~/contexts/error-toast";
import { apiMutate } from "~/utils/apiClient";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API, VITE_ANALYSIS_AGENT_API } = import.meta.env;

const formatDate = (dateStr: string | undefined) => {
  if (!dateStr) {
    return "";
  }
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

const AiAgentsSettings = () => {
  const { data: dataset, mutate } = useDataset();
  const { showError } = useErrorToast();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("instructions");

  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;
  const isEnabled = dataset?.aiAgentsEnabled === true;

  const {
    data: initStatus,
    mutate: refreshInitStatus,
    isLoading: initStatusLoading,
  } = useInitializationStatus(dataset?.id, isEnabled);

  const triggerInitialization = async () => {
    if (!dataset) {
      return;
    }
    try {
      await apiMutate(
        `${VITE_ANALYSIS_AGENT_API}/datasets/${dataset.id}/initialization`,
        "POST",
        {}
      );
      refreshInitStatus();
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to start dataset initialization");
    }
  };

  const triggerReinitialization = async () => {
    if (!dataset) {
      return;
    }
    try {
      await apiMutate(
        `${VITE_CATALOG_API}/datasets/${dataset.id}/agent-metadata`,
        "DELETE",
        undefined,
        { expects204: true }
      );
      await apiMutate(
        `${VITE_ANALYSIS_AGENT_API}/datasets/${dataset.id}/initialization`,
        "POST",
        {}
      );
      refreshInitStatus();
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to reinitialize dataset");
    }
  };

  const handleToggle = async (checked: boolean) => {
    if (!dataset || submitting) {
      return;
    }

    setSubmitting(true);

    try {
      await apiMutate(`${VITE_CATALOG_API}/datasets/${dataset.id}/ai-agents-enabled`, "PATCH", {
        enabled: checked,
      });

      trackEvent("dataset_ai_agents_toggled", {
        dataset_id: dataset.id,
        enabled: checked,
      });

      mutate((currentDataset) => {
        if (!currentDataset || currentDataset.id !== dataset.id) {
          return currentDataset;
        }
        return { ...currentDataset, aiAgentsEnabled: checked };
      });

      // Trigger initialization when enabling
      if (checked) {
        await triggerInitialization();
      }
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to update AI agents settings");
    }

    setSubmitting(false);
  };

  const handleTabChange = (_: SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  return (
    <Stack>
      <T component="h1" fontSize={20} fontWeight={600} lineHeight={1.8}>
        Analysis Agent
      </T>
      <Stack direction="row" alignItems="center" justifyContent="space-between" py={2}>
        <Box>
          <T fontSize={14} fontWeight={600}>
            Enable
          </T>
          <T variant="body2" color="text.secondary">
            Analysis agents are available to users with Owner and Analyze permissions only—not
            Preview.
          </T>
        </Box>
        <Switch
          checked={isEnabled}
          disabled={!isOwner || submitting}
          onChange={(e) => handleToggle(e.target.checked)}
        />
      </Stack>
      {isEnabled && (
        <TabContext value={activeTab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList
              onChange={handleTabChange}
              sx={{
                "& .MuiTabs-indicator": { backgroundColor: "text.primary" },
                "& .MuiTab-root.Mui-selected": { color: "text.primary" },
              }}
            >
              <Tab label="Instructions" value="instructions" />
              <Tab label="Prompts" value="prompts" />
              <Tab label="Featured" value="shortcuts" />
              <Tab label="Tools" value="tools" />
              <Tab label="Skills" value="skills" />
              <Tab label="Overview" value="overview" />
            </TabList>
          </Box>
          <TabPanel value="instructions" sx={{ px: 0, py: 3 }}>
            <AgentInstructionsEditor />
          </TabPanel>
          <TabPanel value="prompts" sx={{ px: 0, py: 3 }}>
            <SamplePromptsTab />
          </TabPanel>
          <TabPanel value="shortcuts" sx={{ px: 0, py: 3 }}>
            <ConversationShortcutsTab />
          </TabPanel>
          <TabPanel value="tools" sx={{ px: 0, py: 3 }}>
            <McpServerToggles />
          </TabPanel>
          <TabPanel value="skills" sx={{ px: 0, py: 3 }}>
            <DatasetSkillsTab />
          </TabPanel>
          <TabPanel value="overview" sx={{ px: 0, py: 3 }}>
            <OverviewTab
              status={initStatus?.status}
              statusLoading={initStatusLoading}
              generatedAt={formatDate(initStatus?.generatedAt)}
              error={initStatus?.error}
              phase={initStatus?.phase}
              progress={initStatus?.progress}
              onInitialize={triggerInitialization}
              onReinitialize={triggerReinitialization}
            />
          </TabPanel>
        </TabContext>
      )}
    </Stack>
  );
};

export default AiAgentsSettings;
